import { useMemo, useState } from "react";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";

import { notify } from "../../../components/Notification";
import { ConfirmPopUp, DeletePopUp } from "../../../components/PopUp";
import { ConfirmDetail } from "../../../components/PopUp/confirmShared";
import { ErrorState } from "../../../components/States";
import { Skeleton } from "../../../components/Skeleton";
import { AccountType } from "../../Accounts/types";
import {
  CreateInvestmentGoalPayload,
  InvestmentGoalResponse,
  useArchiveInvestmentGoalMutation,
  useCreateInvestmentGoalMutation,
  useDeleteInvestmentGoalMutation,
  useGetAccountsQuery,
  useGetInvestmentGoalsQuery,
  useUpdateInvestmentGoalMutation,
} from "../../../services";
import { formatCurrency } from "../../../utils";
import { InvestmentGoalPanel } from "../../Investments/panels";
import { InvestmentGoalCard } from "./InvestmentGoalCard";
import { isSettledInvestmentGoal } from "./investmentGoalStatus";
import { PastGoals } from "./PastGoals";
import { PastInvestmentGoalRow } from "./PastInvestmentGoalRow";
import { InvestmentGoalsProps } from "./types";
import {
  Card,
  CardsGrid,
  EmptyIcon,
  EmptyState,
  EmptyText,
  Header,
  Group,
  Hint,
  PastList,
  Title,
} from "./styles";
import Button, { ButtonTypes } from "../../../components/Button";

// Two placeholder cards while the first fetch is in flight — enough to signal
// "content is loading here" without guessing the real count.
const SKELETON_KEYS = [0, 1];

const goalDetailsOf = (
  goal: InvestmentGoalResponse | null,
): ConfirmDetail | undefined => {
  if (!goal) {
    return undefined;
  }
  return {
    pill: { label: "INVESTMENT GOAL", variant: "DEFAULT" },
    title: goal.name,
    subtitle: `${formatCurrency(goal.targetAmount)} target`,
  };
};

export const InvestmentGoals = ({
  showPast = false,
  isPastScrollTarget = false,
}: InvestmentGoalsProps) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<InvestmentGoalResponse | null>(
    null,
  );
  const [goalToDelete, setGoalToDelete] =
    useState<InvestmentGoalResponse | null>(null);
  const [goalToArchive, setGoalToArchive] =
    useState<InvestmentGoalResponse | null>(null);

  const goalsQuery = useGetInvestmentGoalsQuery();
  const accountsQuery = useGetAccountsQuery();
  const [createGoal] = useCreateInvestmentGoalMutation();
  const [updateGoal] = useUpdateInvestmentGoalMutation();
  const [archiveGoal, { isLoading: isArchiving }] =
    useArchiveInvestmentGoalMutation();
  const [deleteGoal, { isLoading: isDeleting }] =
    useDeleteInvestmentGoalMutation();

  const allGoals = useMemo(() => goalsQuery.data ?? [], [goalsQuery.data]);
  const activeGoals = useMemo(
    () => allGoals.filter((goal) => !isSettledInvestmentGoal(goal)),
    [allGoals],
  );
  const pastGoalList = useMemo(
    () => allGoals.filter(isSettledInvestmentGoal),
    [allGoals],
  );
  const hasGoals = allGoals.length > 0;

  // First load owns the skeleton; a failed first load owns the error state.
  // Background refetches keep the existing cards mounted so the section never
  // flashes empty (the toast still surfaces the underlying API error).
  const showError = goalsQuery.isError && !hasGoals;
  const showSkeleton = goalsQuery.isLoading && !hasGoals && !showError;
  const showEmpty = !goalsQuery.isLoading && !hasGoals && !showError;

  const investmentAccounts = useMemo(
    () =>
      (accountsQuery.data ?? [])
        .filter(
          (account) =>
            account.accountType === AccountType.INVESTMENTS && account.isActive,
        )
        .map((account) => ({
          id: account.id,
          name: account.name,
          balance: account.balance,
          valuation: account.valuation,
        })),
    [accountsQuery.data],
  );

  const openCreate = () => {
    setEditingGoal(null);
    setIsPanelOpen(true);
  };

  const openEdit = (goal: InvestmentGoalResponse) => {
    setEditingGoal(goal);
    setIsPanelOpen(true);
  };

  const closePanel = () => {
    setIsPanelOpen(false);
    setEditingGoal(null);
  };

  const handleSubmit = async (
    payload: CreateInvestmentGoalPayload,
    id?: number,
  ) => {
    try {
      if (id === undefined) {
        await createGoal(payload).unwrap();
        notify.success("Investment goal created!");
      } else {
        await updateGoal({ id, payload }).unwrap();
        notify.success("Investment goal updated!");
      }
      closePanel();
    } catch {
      // errorToastMiddleware surfaces the API error; keep the panel open.
    }
  };

  const confirmDelete = async () => {
    if (!goalToDelete) {
      return;
    }
    try {
      await deleteGoal(goalToDelete.id).unwrap();
      notify.success("Investment goal deleted!");
      setGoalToDelete(null);
    } catch {
      // errorToastMiddleware surfaces the API error; keep the modal open.
    }
  };

  const confirmArchive = async () => {
    if (!goalToArchive) {
      return;
    }
    try {
      const archived = await archiveGoal(goalToArchive.id).unwrap();
      notify.success(
        archived.reachedAt === null
          ? "Investment goal archived"
          : "Goal reached!",
      );
      setGoalToArchive(null);
    } catch {
      // errorToastMiddleware surfaces the API error; keep the modal open.
    }
  };

  const handleSaveReturn = async (
    goal: InvestmentGoalResponse,
    assumedAnnualReturn: number,
  ) => {
    try {
      await updateGoal({
        id: goal.id,
        payload: { assumedAnnualReturn },
      }).unwrap();
      notify.success("Assumed return updated!");
    } catch {
      // errorToastMiddleware surfaces the API error.
    }
  };

  const skeleton = (
    <CardsGrid>
      {SKELETON_KEYS.map((key) => (
        <Card key={key} aria-hidden="true">
          <Skeleton variant="text" width="45%" />
          <Skeleton variant="text" width="65%" height="28px" />
          <Skeleton variant="rectangular" width="100%" height="8px" />
          <Skeleton variant="text" width="55%" />
        </Card>
      ))}
    </CardsGrid>
  );

  const cards = (
    <CardsGrid>
      {activeGoals.map((goal) => (
        <InvestmentGoalCard
          key={goal.id}
          goal={goal}
          onEdit={openEdit}
          onArchive={setGoalToArchive}
          onDelete={setGoalToDelete}
          onSaveReturn={handleSaveReturn}
        />
      ))}
    </CardsGrid>
  );

  const emptyState = (
    <EmptyState>
      <EmptyIcon>
        <TrackChangesRoundedIcon fontSize="large" />
      </EmptyIcon>
      <EmptyText>
        No investment goals yet. Set a target and see when you could reach it.
      </EmptyText>
      <Button
        type={ButtonTypes.primary}
        size="small"
        onClick={openCreate}
        data-testid="add-investment-goal"
      >
        + Add your first goal
      </Button>
    </EmptyState>
  );

  const renderBody = () => {
    if (showError) {
      return (
        <ErrorState
          message="We couldn't load your investment goals."
          onRetry={async () => {
            await goalsQuery.refetch();
          }}
        />
      );
    }
    if (showSkeleton) {
      return skeleton;
    }
    if (showEmpty) {
      return emptyState;
    }
    if (activeGoals.length === 0) {
      return <Hint>No active investment goals.</Hint>;
    }
    return cards;
  };

  const pastGoals = (
    <PastGoals
      count={pastGoalList.length}
      isInitiallyOpen={showPast}
      isScrollTarget={isPastScrollTarget}
    >
      <PastList>
        {pastGoalList.map((goal) => (
          <PastInvestmentGoalRow
            key={goal.id}
            goal={goal}
            onDelete={setGoalToDelete}
          />
        ))}
      </PastList>
    </PastGoals>
  );

  const archiveDetails = goalDetailsOf(goalToArchive);
  const deleteDetails = goalDetailsOf(goalToDelete);

  return (
    <Group>
      <Header>
        <Title>Investment goals</Title>
        {!showEmpty && !showError && (
          <Button
            type={ButtonTypes.primary}
            size="small"
            onClick={openCreate}
            data-testid="add-investment-goal"
          >
            + Add goal
          </Button>
        )}
      </Header>

      {renderBody()}

      {pastGoals}

      {isPanelOpen && (
        <InvestmentGoalPanel
          goal={editingGoal ?? undefined}
          accounts={investmentAccounts}
          isOpen={isPanelOpen}
          onClose={closePanel}
          onSubmit={handleSubmit}
        />
      )}

      <ConfirmPopUp
        isOpen={goalToArchive !== null}
        setIsOpen={() => setGoalToArchive(null)}
        title="Archive investment goal"
        text="Archive goal: it moves to Past with today's value and can't be edited."
        details={archiveDetails}
        confirmLabel="Archive"
        loadingLabel="Archiving…"
        onConfirm={confirmArchive}
        isLoading={isArchiving}
      />

      <DeletePopUp
        isOpen={goalToDelete !== null}
        setIsOpen={() => setGoalToDelete(null)}
        title="Delete investment goal"
        text={
          <>
            Do you really want to delete the{" "}
            <strong>{goalToDelete?.name}</strong> investment goal? This only
            removes the goal — your accounts and their balances are not
            affected.
          </>
        }
        details={deleteDetails}
        handleOnDelete={confirmDelete}
        isLoading={isDeleting}
      />
    </Group>
  );
};
